import {
  zHealthResponse,
  zVersionResponse,
  type HealthResponse,
  type VersionResponse,
} from '@badger-sentinel/protocol';

import { apiUrl } from './config.js';
import { httpFetch } from './transport.js';

/**
 * Every response is parsed with the shared schema, never cast. The client is a
 * renderer (§13.1) — but that does not make the server trusted input, and a schema
 * mismatch after a deploy should surface as a clear error here rather than as
 * `undefined` three components deep.
 */
async function getJson<T>(
  url: string,
  schema: { parse: (value: unknown) => T },
  signal?: AbortSignal,
): Promise<T> {
  const response = await httpFetch(url, {
    method: 'GET',
    headers: { accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  const body: unknown = await response.json();

  // /healthz answers 503 with a well-formed body when the DB is down — that is a
  // successful read of a degraded state, not a transport failure.
  if (!response.ok && response.status !== 503) {
    throw new Error(`${url} responded ${response.status}`);
  }

  return schema.parse(body);
}

export function fetchHealth(baseUrl: string, signal?: AbortSignal): Promise<HealthResponse> {
  return getJson(apiUrl(baseUrl, '/healthz'), zHealthResponse, signal);
}

export function fetchVersion(baseUrl: string, signal?: AbortSignal): Promise<VersionResponse> {
  return getJson(apiUrl(baseUrl, '/api/version'), zVersionResponse, signal);
}
